import { useMemo, useState } from 'react';
import { subDays, subMonths } from 'date-fns';

import {
  getShortAgents,
  getLatencyTimeseries,
  getLatencyPercentiles,
  getLatencyModels,
  getLatencyFallbacks,
  getLatencyToolCalls,
} from '../../../../../api';
import { useFetch } from '../../../../../hooks';
import { useAuth } from '../../../../../shared/User';
import {
  LatencyPeriod,
  findPreset,
  intervalForPeriod,
  metricOptions,
} from '../const';

export type LatencyForm = {
  agentId: string;
  conversationId: string;
  metricKey: string;
  period: LatencyPeriod;
};

const defaultForm: LatencyForm = {
  agentId: '',
  conversationId: '',
  metricKey: metricOptions[0].value,
  period: LatencyPeriod.TODAY,
};

export const useLatencyMetrics = () => {
  const { organization_id } = useAuth();
  const [form, setForm] = useState<LatencyForm>(defaultForm);

  const preset = findPreset(form.metricKey);
  const interval = intervalForPeriod(form.period);

  // Range as RFC3339 with an explicit UTC zone (the Go API parses time.RFC3339,
  // which requires an offset — unlike the "yyyy-MM-dd'T'HH:mm:ss.SSS" the other
  // analytics tab uses).
  const range = useMemo(() => {
    const now = new Date();
    const to = now.toISOString();
    let fromDate: Date;
    switch (form.period) {
      case LatencyPeriod.HALF_YEAR:
        fromDate = subMonths(now, 6);
        break;
      case LatencyPeriod.MONTH:
        fromDate = subDays(now, 30);
        break;
      case LatencyPeriod.WEEK:
        fromDate = subDays(now, 7);
        break;
      default:
        fromDate = subDays(now, 1);
    }
    return { from: fromDate.toISOString(), to };
  }, [form.period]);

  const { loading: isAgentsLoading, data: agentsData } = useFetch(
    async () => {
      const data = await getShortAgents();
      return (data ?? []).map(d => ({ value: d.id ?? '', label: d.title }));
    },
    [organization_id]
  );

  const agentsOptions = useMemo(
    () => [{ value: '', label: 'All agents' }, ...(agentsData ?? [])],
    [agentsData]
  );

  const common = {
    from: range.from,
    to: range.to,
    agentId: form.agentId || undefined,
    conversationId: form.conversationId || undefined,
  };

  const deps = [
    organization_id,
    form.period,
    form.agentId,
    form.conversationId,
    form.metricKey,
  ];

  const timeseries = useFetch(
    () =>
      getLatencyTimeseries({
        ...common,
        metricType: preset.metricType,
        metric: preset.metric,
        aggregation: 'p95',
        interval,
      }),
    deps
  );

  const percentiles = useFetch(
    () =>
      getLatencyPercentiles({
        ...common,
        metricType: preset.metricType,
        metric: preset.metric,
        groupBy: 'model',
      }),
    deps
  );

  const models = useFetch(() => getLatencyModels(common), deps);

  const fallbacks = useFetch(
    () => getLatencyFallbacks({ ...common, groupBy: 'failed_model' }),
    deps
  );

  const toolCalls = useFetch(
    () => getLatencyToolCalls({ ...common, limit: 100 }),
    deps
  );

  return {
    form,
    setForm,
    agentsOptions,
    isAgentsLoading,
    metricOptions,
    preset,
    interval,
    timeseries,
    percentiles,
    models,
    fallbacks,
    toolCalls,
  };
};
